import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Alert,
  Chip,
  CircularProgress,
  Radio,
  RadioGroup,
  FormControlLabel,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Breadcrumbs,
  Link,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Sync as ZohoIcon,
  SwapHoriz as MethodIcon,
  Link as ConnectIcon,
  Business as OrgIcon,
  AccountBalance as BankIcon,
  ContentCopy as CopyIcon,
  History as ActivityIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useEasyCheck } from '../../../context/EasyCheckContext';
import { EasyCheckButton } from '../../common/Controls';
import { zohoService } from '../../../services/zoho/zohoService';
import { canAccess } from '../../../utils/planPermissions';
import { useSnackbar } from '../../../hooks/useSnackbar';
import SnackbarNotification from '../../common/Feedback/SnackbarNotification';
import ZohoWebhookKeysCard from './ZohoWebhookKeysCard';

// Zoho Books integration setup (Business Plus / Premium, Admin only to change).
// Method WEBHOOK (default, existing behaviour) or API_PULL (customer connects their own Zoho Books
// with a read-only Self Client and imports cheque payments from Print Cheques > From Zoho).

const PageContainer = styled(Box)(({ theme }) => ({
  maxWidth: '900px',
  margin: '0 auto',
  padding: theme.spacing(3),
  height: 'calc(100vh - 64px)',
  overflowY: 'auto',
}));

const SettingsCard = styled(Card)(({ theme }) => ({
  borderRadius: theme.spacing(2),
  marginBottom: theme.spacing(3),
  border: `1px solid ${theme.palette.divider}`,
}));

const SectionHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(2),
}));

const DC_LABELS = {
  com: 'United States (zoho.com)',
  eu: 'Europe (zoho.eu)',
  in: 'India (zoho.in)',
  'com.au': 'Australia (zoho.com.au)',
  jp: 'Japan (zoho.jp)',
  sa: 'Saudi Arabia (zoho.sa)',
  ca: 'Canada (zohocloud.ca)',
};

const STATUS_CHIP = {
  CONNECTED: { label: 'Connected', color: 'success' },
  ERROR: { label: 'Needs reconnect', color: 'error' },
  NOT_CONNECTED: { label: 'Not connected', color: 'default' },
};

const formatDateTime = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
};

const ZohoSetupPage = () => {
  const { user } = useEasyCheck();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { snackbar, showSuccess, showError, hideSnackbar } = useSnackbar();
  const planAllowed = canAccess(user?.planId, 'zohoIntegration');
  const isAdmin = user?.role === 'Admin';

  const { data: settings, isLoading, error: settingsError } = useQuery({
    queryKey: ['zohoSettings'],
    queryFn: () => zohoService.getSettings(),
    enabled: planAllowed,
    retry: false,
  });

  const [method, setMethod] = useState('WEBHOOK');
  const [connectForm, setConnectForm] = useState({ dataCentre: 'com', clientId: '', clientSecret: '', grantCode: '' });
  const [showConnectForm, setShowConnectForm] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [orgForm, setOrgForm] = useState({ organizationId: '', chqNoFieldName: '' });
  const [bankData, setBankData] = useState(null); // { zohoAccounts, banks }
  const [mapping, setMapping] = useState({}); // zohoAccountId -> bankId|''
  const [busy, setBusy] = useState(''); // which action is running
  const [disconnectOpen, setDisconnectOpen] = useState(false);

  const connected = settings?.connStatus === 'CONNECTED' && settings?.hasCredentials;
  const hasOrg = Boolean(settings?.organizationId);
  const canEdit = isAdmin && planAllowed;

  useEffect(() => {
    if (!settings) return;
    setMethod(settings.integrationMethod || 'WEBHOOK');
    setOrgForm({ organizationId: settings.organizationId || '', chqNoFieldName: settings.chqNoFieldName || '' });
    setConnectForm((f) => ({ ...f, dataCentre: settings.dataCentre || f.dataCentre, clientId: settings.clientId || f.clientId }));
    setShowConnectForm(!settings.hasCredentials || settings.connStatus !== 'CONNECTED');
  }, [settings]);

  // Organizations + bank accounts are loaded on demand from Zoho once connected
  useEffect(() => {
    if (!canEdit || !connected) return;
    zohoService.getOrganizations().then(setOrganizations).catch((e) => showError(e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canEdit, connected]);

  useEffect(() => {
    if (!canEdit || !connected || !hasOrg) {
      setBankData(null);
      return;
    }
    zohoService
      .getBankAccounts()
      .then((data) => {
        setBankData(data);
        const initial = {};
        data.zohoAccounts.forEach((a) => {
          initial[a.accountId] = a.mappedBankId || '';
        });
        setMapping(initial);
      })
      .catch((e) => showError(e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canEdit, connected, hasOrg, settings?.organizationId]);

  const applySettings = (next) => {
    queryClient.setQueryData(['zohoSettings'], next);
  };

  const run = async (key, fn) => {
    setBusy(key);
    try {
      await fn();
    } catch (e) {
      showError(e.message);
    } finally {
      setBusy('');
    }
  };

  const handleSaveMethod = () =>
    run('method', async () => {
      applySettings(await zohoService.saveMethod(method));
      showSuccess(method === 'API_PULL' ? 'API Pull enabled — use "From Zoho" on the Print Cheques page' : 'Webhook method selected');
    });

  const handleConnect = () =>
    run('connect', async () => {
      const { settings: next, organizations: orgs } = await zohoService.connect(connectForm);
      applySettings(next);
      setOrganizations(orgs);
      setConnectForm((f) => ({ ...f, clientSecret: '', grantCode: '' }));
      showSuccess(next.organizationId ? `Connected to ${next.organizationName}` : 'Connected — now select your organization');
    });

  const handleSaveOrg = () =>
    run('org', async () => {
      applySettings(await zohoService.saveSettings(orgForm));
      showSuccess('Organization settings saved');
    });

  const handleSaveMapping = () =>
    run('mapping', async () => {
      const mappings = bankData.zohoAccounts.map((a) => ({
        zohoAccountId: a.accountId,
        zohoAccountName: a.accountName,
        bankId: mapping[a.accountId] ? Number(mapping[a.accountId]) : null,
      }));
      await zohoService.saveBankMapping(mappings);
      showSuccess('Bank mapping saved');
    });

  const handleDisconnect = () =>
    run('disconnect', async () => {
      applySettings(await zohoService.disconnect());
      setDisconnectOpen(false);
      setOrganizations([]);
      setBankData(null);
      showSuccess('Disconnected from Zoho Books. The integration method is back to Webhook.');
    });

  const copyScopes = () => {
    if (navigator.clipboard && settings?.requiredScopes) {
      navigator.clipboard.writeText(settings.requiredScopes).then(() => showSuccess('Scopes copied'));
    }
  };

  const header = (
    <Box sx={{ mb: 3 }}>
      <Breadcrumbs sx={{ mb: 1 }}>
        <Link component="button" underline="hover" color="inherit" onClick={() => navigate('/preferences')}>
          Preferences
        </Link>
        <Typography color="text.primary">Zoho Books</Typography>
      </Breadcrumbs>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <ZohoIcon sx={{ color: '#2563eb', fontSize: 32 }} />
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Zoho Books Integration
        </Typography>
        {settings && (
          <Chip
            size="small"
            label={STATUS_CHIP[settings.connStatus]?.label || settings.connStatus}
            color={STATUS_CHIP[settings.connStatus]?.color || 'default'}
          />
        )}
      </Box>
    </Box>
  );

  const snackbarEl = (
    <SnackbarNotification open={snackbar.open} message={snackbar.message} severity={snackbar.severity} onClose={hideSnackbar} />
  );

  // Lower plans: API Pull is unavailable, but a company already on the webhook can still manage its keys
  if (!planAllowed) {
    return (
      <PageContainer>
        {header}
        <Alert severity="info" sx={{ mb: 3 }}>
          API Pull is available on the Business Plus and Premium plans.
        </Alert>
        {isAdmin && <ZohoWebhookKeysCard planAllowed={false} showSuccess={showSuccess} showError={showError} />}
        {snackbarEl}
      </PageContainer>
    );
  }

  if (isLoading) {
    return (
      <PageContainer>
        {header}
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      </PageContainer>
    );
  }

  if (settingsError || !settings) {
    return (
      <PageContainer>
        {header}
        <Alert severity="error">{settingsError?.message || 'Failed to load Zoho settings'}</Alert>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      {header}

      {!isAdmin && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Only administrators can change the Zoho Books integration.
        </Alert>
      )}
      {settings.connStatus === 'ERROR' && settings.lastError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {settings.lastError}
        </Alert>
      )}

      {/* 1. Integration method */}
      <SettingsCard>
        <CardContent sx={{ p: 3 }}>
          <SectionHeader>
            <MethodIcon sx={{ color: '#2563eb', fontSize: 28 }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Integration Method
            </Typography>
          </SectionHeader>
          <RadioGroup value={method} onChange={(e) => setMethod(e.target.value)}>
            <FormControlLabel
              value="WEBHOOK"
              disabled={!canEdit}
              control={<Radio />}
              label={
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    Webhook (default)
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Zoho Books sends each cheque payment to EasyCheck automatically through a workflow rule, using the
                    connection details and key in Webhook Connection below.
                  </Typography>
                </Box>
              }
              sx={{ alignItems: 'flex-start', mb: 1.5, '& .MuiRadio-root': { pt: 0.5 } }}
            />
            <FormControlLabel
              value="API_PULL"
              disabled={!canEdit || !connected || !hasOrg}
              control={<Radio />}
              label={
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    API Pull
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    You connect EasyCheck to your Zoho Books (read-only) and import cheque payments yourself with
                    "From Zoho" on the Print Cheques page — review first, then import.
                    {(!connected || !hasOrg) && ' Connect Zoho Books and select an organization below to enable this option.'}
                  </Typography>
                </Box>
              }
              sx={{ alignItems: 'flex-start', '& .MuiRadio-root': { pt: 0.5 } }}
            />
          </RadioGroup>
          {canEdit && (
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <EasyCheckButton
                buttonType="form"
                onClick={handleSaveMethod}
                loading={busy === 'method'}
                disabled={method === settings.integrationMethod || Boolean(busy)}
              >
                Save Method
              </EasyCheckButton>
            </Box>
          )}
        </CardContent>
      </SettingsCard>

      {/* Webhook keys — view / generate / change / deactivate (Admin) */}
      {isAdmin && <ZohoWebhookKeysCard planAllowed showSuccess={showSuccess} showError={showError} />}

      {/* 2. Connect */}
      <SettingsCard>
        <CardContent sx={{ p: 3 }}>
          <SectionHeader>
            <ConnectIcon sx={{ color: '#2563eb', fontSize: 28 }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Connect Zoho Books (API Pull)
            </Typography>
          </SectionHeader>

          {settings.hasCredentials && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Data centre:</strong> {DC_LABELS[settings.dataCentre] || settings.dataCentre} &nbsp;·&nbsp;
                <strong>Client ID:</strong> {settings.clientId}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Connected {formatDateTime(settings.connectedAt)}
                {settings.connectedBy ? ` by ${settings.connectedBy}` : ''}
              </Typography>
              {canEdit && (
                <Box sx={{ mt: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {!showConnectForm && (
                    <EasyCheckButton buttonType="form" variant="outlined" onClick={() => setShowConnectForm(true)}>
                      Reconnect
                    </EasyCheckButton>
                  )}
                  <EasyCheckButton buttonType="form" variant="outlined" color="error" onClick={() => setDisconnectOpen(true)}>
                    Disconnect
                  </EasyCheckButton>
                </Box>
              )}
            </Box>
          )}

          {canEdit && showConnectForm && (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  How to get these values (read-only access)
                </Typography>
                <Typography variant="body2" component="div">
                  1. Sign in to the Zoho API Console (api-console.zoho.com for zoho.com accounts, or the console for your
                  data centre) with a Zoho user that can view payments.
                  <br />
                  2. Choose <strong>Add Client → Self Client</strong> and create it. Copy the <strong>Client ID</strong> and{' '}
                  <strong>Client Secret</strong>.
                  <br />
                  3. Open the <strong>Generate Code</strong> tab and paste these scopes (read-only):
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 1 }}>
                  <Box component="code" sx={{ p: 1, bgcolor: 'background.paper', borderRadius: 1, fontSize: '0.8rem', wordBreak: 'break-all', flex: 1 }}>
                    {settings.requiredScopes}
                  </Box>
                  <Tooltip title="Copy scopes">
                    <IconButton size="small" onClick={copyScopes}>
                      <CopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Typography variant="body2" component="div">
                  4. Set the time duration to 10 minutes, click Create, and paste the generated code below straight away —
                  codes expire within minutes.
                </Typography>
              </Alert>

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                <TextField
                  select
                  size="small"
                  label="Zoho data centre"
                  value={connectForm.dataCentre}
                  onChange={(e) => setConnectForm((f) => ({ ...f, dataCentre: e.target.value }))}
                >
                  {(settings.dataCentres || []).map((dc) => (
                    <MenuItem key={dc} value={dc}>
                      {DC_LABELS[dc] || dc}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  size="small"
                  label="Client ID"
                  value={connectForm.clientId}
                  onChange={(e) => setConnectForm((f) => ({ ...f, clientId: e.target.value }))}
                  autoComplete="off"
                />
                <TextField
                  size="small"
                  label="Client Secret"
                  type="password"
                  value={connectForm.clientSecret}
                  onChange={(e) => setConnectForm((f) => ({ ...f, clientSecret: e.target.value }))}
                  autoComplete="new-password"
                />
                <TextField
                  size="small"
                  label="Grant code"
                  value={connectForm.grantCode}
                  onChange={(e) => setConnectForm((f) => ({ ...f, grantCode: e.target.value }))}
                  autoComplete="off"
                />
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                The Client Secret and Zoho token are stored encrypted and are never shown again. You can revoke access
                at any time from the Zoho API Console.
              </Typography>
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                {settings.hasCredentials && (
                  <EasyCheckButton buttonType="form" variant="outlined" onClick={() => setShowConnectForm(false)}>
                    Cancel
                  </EasyCheckButton>
                )}
                <EasyCheckButton
                  buttonType="form"
                  onClick={handleConnect}
                  loading={busy === 'connect'}
                  disabled={!connectForm.clientId || !connectForm.clientSecret || !connectForm.grantCode || Boolean(busy)}
                >
                  Connect
                </EasyCheckButton>
              </Box>
            </>
          )}

          {!settings.hasCredentials && !canEdit && (
            <Typography variant="body2" color="text.secondary">
              Zoho Books is not connected.
            </Typography>
          )}
        </CardContent>
      </SettingsCard>

      {/* 3. Organization + cheque number field */}
      {connected && (
        <SettingsCard>
          <CardContent sx={{ p: 3 }}>
            <SectionHeader>
              <OrgIcon sx={{ color: '#2563eb', fontSize: 28 }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Organization
              </Typography>
            </SectionHeader>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <TextField
                select
                size="small"
                label="Zoho organization"
                value={organizations.some((o) => o.organizationId === orgForm.organizationId) ? orgForm.organizationId : ''}
                onChange={(e) => setOrgForm((f) => ({ ...f, organizationId: e.target.value }))}
                disabled={!canEdit}
                helperText={!organizations.length ? settings.organizationName || 'Loading organizations…' : ' '}
              >
                {organizations.map((o) => (
                  <MenuItem key={o.organizationId} value={o.organizationId}>
                    {o.name} {o.currencyCode ? `(${o.currencyCode})` : ''}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                size="small"
                label="Cheque number custom field (optional)"
                value={orgForm.chqNoFieldName}
                onChange={(e) => setOrgForm((f) => ({ ...f, chqNoFieldName: e.target.value }))}
                disabled={!canEdit}
                helperText="Leave blank to read the cheque number from the payment Reference#"
              />
            </Box>
            {canEdit && (
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <EasyCheckButton
                  buttonType="form"
                  onClick={handleSaveOrg}
                  loading={busy === 'org'}
                  disabled={!orgForm.organizationId || Boolean(busy)}
                >
                  Save Organization
                </EasyCheckButton>
              </Box>
            )}
          </CardContent>
        </SettingsCard>
      )}

      {/* 4. Bank mapping */}
      {connected && hasOrg && canEdit && (
        <SettingsCard>
          <CardContent sx={{ p: 3 }}>
            <SectionHeader>
              <BankIcon sx={{ color: '#2563eb', fontSize: 28 }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Bank Mapping
              </Typography>
            </SectionHeader>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Choose which EasyCheck bank each Zoho bank account prints on. Unmapped accounts are matched by exact
              account name; payments from an account that matches nothing are shown as "Bank not mapped" and are not
              imported.
            </Typography>
            {!bankData ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={28} />
              </Box>
            ) : bankData.zohoAccounts.length === 0 ? (
              <Alert severity="info">No bank accounts were found in this Zoho organization.</Alert>
            ) : (
              <>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Zoho bank account</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Currency</TableCell>
                        <TableCell sx={{ fontWeight: 600, width: '45%' }}>EasyCheck bank</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {bankData.zohoAccounts.map((a) => (
                        <TableRow key={a.accountId}>
                          <TableCell>
                            {a.accountName}
                            {a.accountType && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                {a.accountType}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>{a.currencyCode}</TableCell>
                          <TableCell>
                            <TextField
                              select
                              size="small"
                              fullWidth
                              value={mapping[a.accountId] || ''}
                              onChange={(e) => setMapping((m) => ({ ...m, [a.accountId]: e.target.value }))}
                            >
                              <MenuItem value="">
                                <em>Match by account name</em>
                              </MenuItem>
                              {bankData.banks.map((b) => (
                                <MenuItem key={b.id} value={b.id}>
                                  {b.accountName} ({b.currency})
                                </MenuItem>
                              ))}
                            </TextField>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                  <EasyCheckButton buttonType="form" onClick={handleSaveMapping} loading={busy === 'mapping'} disabled={Boolean(busy)}>
                    Save Mapping
                  </EasyCheckButton>
                </Box>
              </>
            )}
          </CardContent>
        </SettingsCard>
      )}

      {/* 5. Activity */}
      {settings.hasCredentials && (
        <SettingsCard>
          <CardContent sx={{ p: 3 }}>
            <SectionHeader>
              <ActivityIcon sx={{ color: '#2563eb', fontSize: 28 }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Import Activity
              </Typography>
            </SectionHeader>
            <Typography variant="body2">
              Last preview / import: {formatDateTime(settings.lastPullAt)}
              {settings.lastPullBy ? ` by ${settings.lastPullBy}` : ''}
            </Typography>
          </CardContent>
        </SettingsCard>
      )}

      <Dialog open={disconnectOpen} onClose={() => setDisconnectOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Disconnect Zoho Books?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            The stored Zoho credentials will be removed and the integration method will switch back to Webhook. Cheques
            already imported are not affected. You can connect again at any time.
          </Typography>
        </DialogContent>
        <DialogActions>
          <EasyCheckButton buttonType="form" variant="outlined" onClick={() => setDisconnectOpen(false)}>
            Cancel
          </EasyCheckButton>
          <EasyCheckButton buttonType="form" color="error" onClick={handleDisconnect} loading={busy === 'disconnect'}>
            Disconnect
          </EasyCheckButton>
        </DialogActions>
      </Dialog>

      {snackbarEl}
    </PageContainer>
  );
};

export default ZohoSetupPage;
